import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CartSummary = ({ 
  cartItems = [], 
  onUpdateQuantity = () => {}, 
  onRemoveItem = () => {},
  isCollapsed = false,
  onToggleCollapse = () => {}
}) => {
  const calculateSubtotal = () => {
    return cartItems?.reduce((total, item) => total + (item?.price * item?.quantity), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateFees = () => {
    return cartItems?.length > 0 ? 2.50 : 0; // Processing fee
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() + calculateFees();
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2">
      {/* Mobile Collapsible Header */}
      <div className="lg:hidden">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-between p-4 border-b border-border"
        >
          <div className="flex items-center space-x-2">
            <Icon name="ShoppingCart" size={20} className="text-primary" />
            <span className="font-semibold text-foreground">Order Summary</span>
            <span className="text-sm text-muted-foreground">({cartItems?.length} items)</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-foreground">${calculateTotal()?.toFixed(2)}</span>
            <Icon 
              name={isCollapsed ? "ChevronDown" : "ChevronUp"} 
              size={20} 
              className="text-muted-foreground" 
            />
          </div>
        </button>
      </div>
      {/* Cart Content */}
      <div className={`${isCollapsed ? 'hidden' : 'block'} lg:block`}>
        <div className="p-6">
          <div className="hidden lg:flex items-center space-x-2 mb-6">
            <Icon name="ShoppingCart" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Order Summary</h2>
            <span className="text-sm text-muted-foreground">({cartItems?.length} items)</span>
          </div>

          {/* Cart Items */}
          <div className="space-y-4 mb-6">
            {cartItems?.map((item) => (
              <div key={item?.id} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
                <div className="flex-shrink-0">
                  <Image
                    src={item?.image}
                    alt={item?.name}
                    className="w-16 h-16 rounded-md object-cover"
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground truncate">{item?.name}</h3>
                  <p className="text-sm text-muted-foreground">{item?.category}</p>
                  {item?.details && (
                    <p className="text-xs text-muted-foreground mt-1">{item?.details}</p>
                  )}
                  
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => onUpdateQuantity(item?.id, Math.max(0, item?.quantity - 1))}
                        disabled={item?.quantity <= 1}
                        iconName="Minus"
                        className="w-8 h-8 p-0"
                      />
                      <span className="text-sm font-medium text-foreground min-w-[20px] text-center">
                        {item?.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => onUpdateQuantity(item?.id, item?.quantity + 1)}
                        iconName="Plus"
                        className="w-8 h-8 p-0"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-foreground">
                        ${(item?.price * item?.quantity)?.toFixed(2)}
                      </span>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onRemoveItem(item?.id)}
                        iconName="Trash2"
                        className="text-error hover:text-error w-8 h-8 p-0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Price Breakdown */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-foreground">${calculateSubtotal()?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tax</span>
              <span className="text-foreground">${calculateTax()?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Processing Fee</span>
              <span className="text-foreground">${calculateFees()?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
              <span className="text-foreground">Total</span>
              <span className="text-foreground">${calculateTotal()?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartSummary;